import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";

import CommonSelect from "../../../core/common/commonSelect";
import { getDefaultForm } from "../../../core/common/formSelection";
import api from "../../../services/api";
import CollapseHeader from "../../../core/common/collapse-header/collapse-header";
import Datatable from "../../../core/common/dataTable/index";
import { showBasicAlert } from "../../../core/common/useAlert";
import { all_routes } from "../../router/all_routes";
import { callActions } from "../../../core/data/redux/live-call/slice";
import { acquireCallLock, getCallSocket, isCallLockedByOtherTab } from "./services/callSocket";
import { startNextCallRef } from "./services/callSessionRefs";
import type { AppDispatch } from "../../../core/data/redux/store";

import LeadActivityModal from "./components/LeadActivityModal";
import { LEAD_SOURCES as LEAD_SOURCE_OPTIONS } from "./constants/callConstants";
import { notify } from "../../../services/toast.config";
import LeadsGridFilters from "../leads/components/LeadsGridFilters";
import { SlidersHorizontal } from "lucide-react";
import ReminderDropdown from "../leads/components/ReminderDropdown";

const EMPTY_FORM = { form_id: "", form_name: "Select Lead Form", pipeline_id: "", structure: [] };

const LeadsCallPage = () => {
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();
  const signature = localStorage.getItem("signature");
  const user = useSelector((s: any) => s.user?.user);

  // ── Redux call state ──────────────────────────────────────────────────────
  const call = useSelector((s: any) => s.liveCall);
  const isAutoCallRef = useRef(false);
  const autoCallStoppedRef = useRef(false);
  const callDisconnectedDataRef = useRef<any>(null);


  useEffect(() => { isAutoCallRef.current = call.isAutoCall; }, [call.isAutoCall]);
  useEffect(() => { autoCallStoppedRef.current = call.autoCallStopped; }, [call.autoCallStopped]);
  useEffect(() => { callDisconnectedDataRef.current = call.callDisconnectedData; }, [call.callDisconnectedData]);

  // ── Page-level state ──────────────────────────────────────────────────────
  const [isLoading, setIsLoading] = useState(false);
  const [companyList, setCompanyList] = useState<any[]>([]);
  const [selectedCompany, setSelectedCompany] = useState({ companyId: "", companyName: "Select Company", companyType: "" });
  const [formsList, setFormsList] = useState<any[]>([]);
  const [selectedForm, setSelectedForm] = useState(EMPTY_FORM);
  const [leadsData, setLeadsData] = useState<any[]>([]);
  const [filteredLeads, setFilteredLeads] = useState<any[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [transferLeadsId, setTransferLeadsId] = useState<any[]>([]);
  const [pipelineStages, setPipelineStages] = useState<string[]>([]);
  const [followUpFilter, setFollowUpFilter] = useState<string>("all"); // "all", "today", "overdue", "upcoming", "custom", "none"
  const [customFollowUpDate, setCustomFollowUpDate] = useState<moment.Moment | null>(null);
  const [reminderLeadIds, setReminderLeadIds] = useState<{ today: number[], overdue: number[], upcoming: number[] } | null>(null);
  const [reminderLeads, setReminderLeads] = useState<{ today: any[], overdue: any[], upcoming: any[] } | null>(null);
  const [isReminderLoading, setIsReminderLoading] = useState(false);
  const [activityModalLead, setActivityModalLead] = useState<any>(null);
  const [callLogsMap, setCallLogsMap] = useState<Map<string, any>>(new Map());
  const [filters, setFilters] = useState({
    user_id: "0", user_name: "All Employees",
    leadSource: undefined as string | undefined,
    startDate: moment().subtract(29, "days"), endDate: moment(),
    showOnlyTransferred: false, searchQuery: "", pipelineStage: "",
  });

  const [showCallFilters, setShowCallFilters] = useState(false);
  const [tempSearchQuery, setTempSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState("Recently Added");

  const [activityFilter, setActivityFilter] = useState<string>("all");
  const [activityStartDate, setActivityStartDate] = useState<moment.Moment>(moment().subtract(29, "days"));
  const [activityEndDate, setActivityEndDate] = useState<moment.Moment>(moment());
  const [activityLeadIds, setActivityLeadIds] = useState<number[] | null>(null);
  const [isActivityLoading, setIsActivityLoading] = useState(false);
  const [reminderDropdownOpened, setReminderDropdownOpened] = useState(false);
  const [reminderCustomStart, setReminderCustomStart] = useState<moment.Moment | null>(null);
  const [reminderCustomEnd, setReminderCustomEnd] = useState<moment.Moment | null>(null);
  const reminderLoadedForFormRef = useRef<string>("");

  const selectedFormRef = useRef(selectedForm);
  useEffect(() => { selectedFormRef.current = selectedForm; }, [selectedForm]);

  const followUpOptions = [
    { value: "today", label: "Today" },
    { value: "upcoming", label: "Upcoming" },
    { value: "overdue", label: "Overdue" },
  ];

  const activityFilterOptions = [
    { value: "all", label: "All Activity" },
    { value: "today", label: "Today's Activity" },
    { value: "yesterday", label: "Yesterday's Activity" },
    { value: "custom", label: "Custom Date" },
  ];



  const filterByFollowUp = useCallback((lead: any) => {
    if (followUpFilter === "all") return true;

    const followUpDate = lead.follow_up_date;

    if (!followUpDate) return followUpFilter === "none";
    if (followUpFilter === "none") return false;

    if (reminderLeadIds && ["today", "overdue", "upcoming"].includes(followUpFilter)) {
      const leadId = Number(lead.key || lead.id);
      if (followUpFilter === "today") return reminderLeadIds.today.includes(leadId);
      if (followUpFilter === "overdue") return reminderLeadIds.overdue.includes(leadId);
      if (followUpFilter === "upcoming") return reminderLeadIds.upcoming.includes(leadId);
    }

    const leadMoment = moment(followUpDate);
    const leadLocal = leadMoment.local();
    const leadDateStr = leadLocal.format("YYYY-MM-DD");
    const todayStr = moment().format("YYYY-MM-DD");

    switch (followUpFilter) {
      case "today":
        return leadDateStr === todayStr;

      case "upcoming":
        return leadLocal.isAfter(moment().endOf('day'));

      case "overdue":
        return leadLocal.isBefore(moment().startOf('day'));

      // case "custom":
      //   if (!customFollowUpDate) return true;
      //   const customDateStr = customFollowUpDate.format("YYYY-MM-DD");
      //   return leadDateStr === customDateStr;

      case "custom":
        if (!reminderCustomStart || !reminderCustomEnd) return true;
        const leadMomentCheck = moment(followUpDate).local();
        return leadMomentCheck.isSameOrAfter(reminderCustomStart, "day") &&
          leadMomentCheck.isSameOrBefore(reminderCustomEnd, "day");

      default:
        return true;
    }
  }, [followUpFilter, customFollowUpDate, reminderLeadIds]);


  const leadMgmtLeadsRef = useRef<any[]>([]);

  // ── handleCall ────────────────────────────────────────────────────────────
  const handleCall = useCallback(async (record: any) => {
    if (call.activeCall) return;

    if (isCallLockedByOtherTab()) {
      notify.error("A call is already active in another tab. Please end it first.");
      return;
    }

    acquireCallLock(user?.userDetails?.id);

    let leadActivities: any[] = [];
    try {
      const res = await api.post("leadService/getLeadActivities", {
        signature,
        lead_id: record.id,
      });
      leadActivities = res.data.data.data || [];
    } catch (e) { }

    const callRecord = {
      ...record,
      mobileNumber: record.mobile_no || record.mobileNumber,
      name: record.response?.["Full Name"] || record.name || "Unknown",
      id: record.id || null,
      activities: leadActivities || [],
      form: selectedFormRef.current,
    };
    getCallSocket().emit("startCall", {
      userId: user?.userDetails?.id,
      data: callRecord,
    });
    dispatch(callActions.setActiveCall(callRecord));
    dispatch(callActions.setIsAutoCall(isAutoCallRef.current));
    dispatch(callActions.setIsLeadsMode(true));
  }, [user, dispatch, call.activeCall]);


  useEffect(() => {
    startNextCallRef.current = () => {
      const leads = leadMgmtLeadsRef.current;
      const current = callDisconnectedDataRef.current;
      const currentId = current?.id;
      const idx = leads.findIndex((l: any) => (l.id) === currentId);
      const next = leads[idx + 1];
      if (next) {
        handleCall(next);
      } else {
        dispatch(callActions.setIsAutoCall(false));
        dispatch(callActions.setAutoCallStopped(true));
        window.dispatchEvent(new CustomEvent('refreshCallsAfterSubmission'));
      }
    };
  }, [handleCall, dispatch]);

  // ── Effects ───────────────────────────────────────────────────────────────
  useEffect(() => {
    fetchCompanyList();
  }, []);

  useEffect(() => {
    if (!selectedCompany.companyId) return;
    setLeadsData([]); setFilteredLeads([]); setUsers([]);
    setPipelineStages([]); setCallLogsMap(new Map());
    setFormsList([]); setSelectedForm(EMPTY_FORM);
    fetchFormsForCompany(selectedCompany);
  }, [selectedCompany.companyId]);

  useEffect(() => {
    if (selectedForm.form_id && selectedForm.pipeline_id) {
      // Reset reminder cache when form changes
      reminderLoadedForFormRef.current = "";
      setReminderLeadIds(null);
      setReminderLeads(null);
      fetchLeads();
    }
  }, [selectedForm.form_id, selectedForm.pipeline_id, filters.user_id, filters.leadSource, filters.startDate, filters.endDate, filters.showOnlyTransferred]);

  useEffect(() => {
    const fetchReminderLeadIds = async () => {
      if (!reminderDropdownOpened) return;
      if (!selectedForm.form_id) return;
      // Skip re-fetching if data is already loaded for this form
      if (reminderLoadedForFormRef.current === selectedForm.form_id && reminderLeads !== null) {
        setReminderDropdownOpened(false);
        return;
      }

      try {
        setIsReminderLoading(true);
        const response = await api.post("leadService/getLeadReminder", {
          signature,
          company_id: selectedCompany.companyId,
          company_type: selectedCompany.companyType,
          form_id: selectedForm.form_id,
          limit: 100000,
        });
        if (response.data?.message?.message === "Data Found" && response.data?.message?.reminder) {
          const { today, due, upcoming } = response.data.message.reminder;

          // Deduplicate each bucket by lead key — the API returns one row per reminder_id,
          // so the same lead can appear multiple times per bucket.
          const dedup = (arr: any[]) => Array.from(new Map(arr.map((l: any) => [Number(l.key), l])).values());
          const todayUniq    = dedup(today    || []);
          const overdueUniq  = dedup(due      || []);
          const upcomingUniq = dedup(upcoming || []);

          setReminderLeadIds({
            today:    todayUniq.map((l: any) => Number(l.key)),
            overdue:  overdueUniq.map((l: any) => Number(l.key)),
            upcoming: upcomingUniq.map((l: any) => Number(l.key)),
          });
          setReminderLeads({
            today:    todayUniq,
            overdue:  overdueUniq,
            upcoming: upcomingUniq,
          });
          reminderLoadedForFormRef.current = selectedForm.form_id;
        } else {
          setReminderLeadIds(null);
          setReminderLeads(null);
        }
      } catch (error) {
        console.error("Error fetching reminder lead IDs:", error);
        setReminderLeadIds(null);
        setReminderLeads(null);
      } finally {
        setIsReminderLoading(false);
        setReminderDropdownOpened(false); // reset trigger after fetch
      }
    };

    fetchReminderLeadIds();
  }, [reminderDropdownOpened, selectedForm.form_id, selectedCompany.companyId, selectedCompany.companyType, signature]);

  useEffect(() => {
    const fetchActivityLeadIds = async () => {
      if (activityFilter === "all") {
        setActivityLeadIds(null);
        return;
      }

      if (activityFilter === "custom" && (!activityStartDate || !activityEndDate)) {
        return;
      }

      try {
        setIsActivityLoading(true);
        let startDateStr = "";
        let endDateStr = "";

        if (activityFilter === "today") {
          startDateStr = moment().startOf('day').format("YYYY-MM-DD");
          endDateStr = moment().add(1, "days").startOf('day').format("YYYY-MM-DD");
        } else if (activityFilter === "yesterday") {
          startDateStr = moment().subtract(1, "days").startOf('day').format("YYYY-MM-DD");
          endDateStr = moment().startOf('day').format("YYYY-MM-DD");
        } else if (activityFilter === "custom" && activityStartDate && activityEndDate) {
          startDateStr = activityStartDate.format("YYYY-MM-DD");
          endDateStr = moment(activityEndDate).add(1, "days").format("YYYY-MM-DD");
        }

        const response = await api.post(`leadService/getLeadIdsByActivity`, {
          signature,
          company_id: selectedCompany.companyId,
          company_type: selectedCompany.companyType,
          form_id: selectedForm.form_id,
          activity_start_date: startDateStr,
          activity_end_date: endDateStr,
        });

        if (response.data?.msg === "Data Found" && response.data?.data?.lead_ids) {
          setActivityLeadIds(response.data.data.lead_ids);
        } else {
          setActivityLeadIds([]);
        }
      } catch (error) {
        console.error("Error fetching activity lead IDs:", error);
        setActivityLeadIds([]);
      } finally {
        setIsActivityLoading(false);
      }
    };

    if (selectedCompany.companyId && selectedForm.form_id) {
      fetchActivityLeadIds();
    }
  }, [activityFilter, activityStartDate, activityEndDate, selectedCompany.companyId, selectedCompany.companyType, selectedForm.form_id, signature]);

  useEffect(() => {
    if (selectedForm.form_id && selectedForm.pipeline_id) fetchPipelineStages();
  }, [selectedForm.form_id, selectedForm.pipeline_id]);

  useEffect(() => { leadMgmtLeadsRef.current = filteredLeads; }, [filteredLeads]);

  // Listen for form submission refresh event
  useEffect(() => {
    const handleRefreshAfterSubmission = () => {
      fetchLeads();
    };

    window.addEventListener('refreshCallsAfterSubmission', handleRefreshAfterSubmission);

    return () => {
      window.removeEventListener('refreshCallsAfterSubmission', handleRefreshAfterSubmission);
    };
  }, [
    selectedForm.form_id,
    selectedForm.pipeline_id,
    selectedCompany.companyId,
    filters.user_id,
    filters.leadSource,
    filters.startDate,
    filters.endDate,
    filters.showOnlyTransferred
  ]);

  // ── API ───────────────────────────────────────────────────────────────────
  const fetchCompanyList = async () => {
    setIsLoading(true);
    try {
      const res = await api.post("userService/getAllCompanyList", { signature });
      const data = res.data.data.data;
      setCompanyList(data);
      setSelectedCompany({ companyId: data[0].id, companyName: data[0].name, companyType: data[0].type });
    } catch (e) { console.error("Error fetching companies:", e); }
    finally { setIsLoading(false); }
  };

  const fetchFormsForCompany = async (company: typeof selectedCompany) => {
    setIsLoading(true);
    try {
      const res = await api.post("leadService/getLeadFormsByCompany", { signature, company_id: company.companyId, company_type: company.companyType });
      const forms = res.data.data.data || [];
      setFormsList(forms);
      if (forms.length > 0) {
        const first = getDefaultForm(forms);
        const nextForm = { form_id: first.key, form_name: first.LeadFormName, pipeline_id: first.pipeline_id, structure: first?.structure };
        setSelectedForm(nextForm);
        await fetchLeadsWithParams(company, nextForm);
      } else {
        setSelectedForm({ form_id: "", form_name: "No Lead Form Available", pipeline_id: "", structure: [] });
        setLeadsData([]); setFilteredLeads([]); setPipelineStages([]);
      }
    } catch (e) {
      console.error("Error fetching forms:", e);
      showBasicAlert("Failed to load forms");
      setFormsList([]);
      setSelectedForm({ form_id: "", form_name: "No Lead Form Available", pipeline_id: "", structure: [] });
      setLeadsData([]); setFilteredLeads([]); setPipelineStages([]);
    } finally { setIsLoading(false); }
  };

  const getLeadSourceFilter = useCallback(() => {
    if (!filters.leadSource) return {};
    const parentValues = LEAD_SOURCE_OPTIONS.map((i) => i.value);
    return parentValues.includes(filters.leadSource) ? { parent_source: filters.leadSource } : { child_source: filters.leadSource };
  }, [filters.leadSource]);

  const fetchCallLogs = async (company: typeof selectedCompany, mobileNumbers: string[]) => {
    if (!company.companyId || !signature || mobileNumbers.length === 0) return;
    try {
      const res = await api.post("callLogsService/getCallDatesAndUpdatedDates", { signature, company_id: parseInt(company.companyId), company_type: company.companyType, mobile_numbers: mobileNumbers });
      const callLogs = res.data.data.all_calls || [];
      const callMap = new Map();
      callLogs.forEach((log: any) => {
        const mobile = log.customer_mobile;
        if (!callMap.has(mobile) || moment(log.call_date).isAfter(moment(callMap.get(mobile).call_date))) callMap.set(mobile, log);
      });
      setCallLogsMap(callMap);
    } catch (e) { console.error("Error fetching call logs:", e); }
  };

  const fetchLeadsWithParams = async (company: typeof selectedCompany, form: typeof selectedForm) => {
    if (!form.form_id || !form.pipeline_id || !company.companyId) return;
    setIsLoading(true);
    try {
      const [settingsRes, leadsRes] = await Promise.all([
        api.post("leadService/getAllLeadSettings", { signature, company_id: parseInt(company.companyId), company_type: company.companyType }),
        api.post("leadService/getAllLeadsResponse", { signature, company_id: company.companyId, company_type: company.companyType, start_date: filters.startDate.format("YYYY-MM-DD"), end_date: filters.endDate.format("YYYY-MM-DD"), form_id: form.form_id, ...(filters.user_id !== "0" && { user_id: filters.user_id }), ...getLeadSourceFilter(), limit: 100000 }),
        api.post("leadService/getTransferLeadsByUser", { signature, company_id: company.companyId, company_type: company.companyType, form_id: form.form_id }),
      ]);
      const fetchedLeads = leadsRes.data.data.data || [];
      setLeadsData(fetchedLeads);
      setTransferLeadsId(leadsRes.data.data.transferLeadIds || []);
      const mobileNumbers = fetchedLeads.map((l: any) => l.mobile_no).filter((m: string) => m?.trim());
      if (mobileNumbers.length > 0) await fetchCallLogs(company, mobileNumbers);
      if (users.length === 0) {
        const userRes = await api.post("userService/getLeadEmployeesListData", { signature, department: settingsRes?.data?.data?.data[0]?.settings?.lead_assigned_to_department || -1, company_id: parseInt(company.companyId), company_type: company.companyType });
        setUsers(userRes.data?.data?.data?.allUsers || []);
      }
    } catch (e) { console.error("Error fetching leads:", e); showBasicAlert("Failed to load leads"); }
    finally { setIsLoading(false); }
  };

  const fetchLeads = async () => {
    if (!selectedForm.form_id || !selectedForm.pipeline_id || !selectedCompany.companyId) return;
    await fetchLeadsWithParams(selectedCompany, selectedForm);
  };

  const fetchPipelineStages = async () => {
    try {
      const res = await api.post("leadService/getAllPipelinesById", { signature, id: selectedForm.pipeline_id });
      setPipelineStages(res.data.data.data[0]?.Stages || []);
    } catch (e) { console.error("Error fetching pipeline stages:", e); }
  };

  // ── Client-side filter ────────────────────────────────────────────────────
  useEffect(() => {
    if (!leadsData.length && !(reminderLeads && ["today", "overdue", "upcoming", "custom"].includes(followUpFilter))) {
      setFilteredLeads([]);
      return;
    }
    let result = [...leadsData];

    const isReminderFilterActive = ["today", "overdue", "upcoming", "custom"].includes(followUpFilter);

    if (isReminderFilterActive && reminderLeads) {
      if (followUpFilter === "today") {
        result = [...reminderLeads.today];
      } else if (followUpFilter === "overdue") {
        result = [...reminderLeads.overdue];
      } else if (followUpFilter === "upcoming") {
        result = [...reminderLeads.upcoming];
      } else if (followUpFilter === "custom") {
        const allReminders = [...(reminderLeads.today || []), ...(reminderLeads.overdue || []), ...(reminderLeads.upcoming || [])];
        if (!reminderCustomStart || !reminderCustomEnd) {
          result = allReminders;
        } else {
          result = allReminders.filter((lead: any) => {
            const followUpDate = lead.follow_up_date;
            if (!followUpDate) return false;
            const leadMomentCheck = moment(followUpDate).local();
            return leadMomentCheck.isSameOrAfter(reminderCustomStart, "day") &&
              leadMomentCheck.isSameOrBefore(reminderCustomEnd, "day");
          });
        }
        const uniqueReminders = new Map(result.map((item: any) => [item.key || item.id, item]));
        result = Array.from(uniqueReminders.values());
      }
    } else {
      // Apply follow-up filter
      result = result.filter(filterByFollowUp);
    }

    if (filters.pipelineStage) result = result.filter((l: any) => l.pipeline_char == filters.pipelineStage);
    if (filters.showOnlyTransferred) result = result.filter((l: any) => transferLeadsId.includes(l.lead_id));
    if (filters.searchQuery) {
      const q = filters.searchQuery.toLowerCase();
      result = result.filter((l: any) => Object.values(l).some((v) => typeof v === "string" && v.toLowerCase().includes(q)) || l.response?.["Full Name"]?.toLowerCase().includes(q) || l.mobile_no?.includes(filters.searchQuery));
    }
    if (activityLeadIds !== null) {
      result = result.filter((lead: any) => activityLeadIds.includes(Number(lead.key || lead.id)));
    }

    const withCallInfo = result.map((lead: any) => {
      const latest = callLogsMap.get(lead.mobile_no);
      return {
        ...lead,
        latest_call_date: latest?.call_date || null,
        latest_call_updated: latest?.updated_at || null,
        has_calls: !!latest
      };
    });

    setFilteredLeads(withCallInfo.sort((a, b) => {
      if (!a.latest_call_date && b.latest_call_date) return -1;
      if (a.latest_call_date && !b.latest_call_date) return 1;
      if (!a.latest_call_date && !b.latest_call_date) return 0;

      const dateA = moment(a.latest_call_date).format("YYYY-MM-DD");
      const dateB = moment(b.latest_call_date).format("YYYY-MM-DD");

      if (dateA !== dateB) {
        return moment(a.latest_call_date).valueOf() - moment(b.latest_call_date).valueOf();
      }

      return moment(a.latest_call_date).valueOf() - moment(b.latest_call_date).valueOf();
    }));
  }, [leadsData, filters.pipelineStage, filters.showOnlyTransferred, filters.searchQuery, transferLeadsId, callLogsMap, filterByFollowUp, activityLeadIds, followUpFilter, reminderLeads, reminderCustomStart, reminderCustomEnd]);

  const activeFilterCount = useMemo(() => [
    filters.user_id !== "0" ? filters.user_id : null,
    followUpFilter !== "all" ? followUpFilter : null,
    activityFilter !== "all" ? activityFilter : null,
    sortBy !== "Recently Added" ? sortBy : null,
    filters.searchQuery,
    filters.leadSource,
    filters.showOnlyTransferred ? "transferred" : null,
  ].filter(Boolean).length, [filters, followUpFilter, activityFilter, sortBy]);



  // ── Handlers ─────────────────────────────────────────────────────────────
  const handleCompanyChange = (opt: any) => {
    if (!opt) return;
    setSelectedCompany({ companyId: opt.value, companyName: opt.label.split("-")[0].trim(), companyType: opt.label.split("-")[1].trim() });
  };

  const handleFormChangeFromHeader = (opt: any) => {
    if (!opt?.value) return;
    const selectedOption = opt.value.split("-");
    const selectedForm = formsList.find((elem) => elem.key == selectedOption?.[1]);
    setSelectedForm({ form_id: selectedOption?.[1], form_name: opt.label, pipeline_id: selectedOption?.[0], structure: selectedForm.structure });
  };

  const handleClearFilters = () => {
    setFilters({
      user_id: "0", user_name: "All Employees", leadSource: undefined, startDate: moment().subtract(29, "days"), endDate: moment(), showOnlyTransferred: false, searchQuery: "", pipelineStage: ""
    });
    setFollowUpFilter('all');
    setActivityFilter('all');
    setActivityStartDate(moment().subtract(29, "days"));
    setActivityEndDate(moment());
    setCustomFollowUpDate(null);
    setTempSearchQuery("");
  }

  const handleSearchApply = useCallback(() => {
    setFilters(p => ({ ...p, searchQuery: tempSearchQuery }));
  }, [tempSearchQuery]);

  // ── Derived ───────────────────────────────────────────────────────────────
  const formOptions = useMemo(() => {
    if (formsList.length === 0) return [{ value: "", label: "No forms available" }];
    return formsList.map((f: any) => ({ value: `${f.pipeline_id}-${f.key}`, label: f.LeadFormName }));
  }, [formsList]);

  const pipelineStageOptions = useMemo(() => [
    { value: "", label: "All Stages" },
    ...pipelineStages.map((s) => ({ value: s, label: s })),
  ], [pipelineStages]);

  const userChoose = useMemo(() => {
    const sForm = formsList.find(f => f.key == selectedForm.form_id);

    if (!sForm?.employees || sForm.employees.length === 0) {
      return [{ value: "0", label: "All Employees" }, ...users.map((u: any) => ({ value: u.key || u.id, label: u.Name || (u.first_name + " " + (u.last_name || "")) }))];
    }

    const filteredUsers = users.filter((user: any) =>
      sForm.employees.includes(user.key || user.id)
    );

    return [
      { value: "0", label: "All Employees" },
      ...filteredUsers.map((u: any) => ({ value: u.key || u.id, label: u.Name || (u.first_name + " " + (u.last_name || "")) }))
    ];
  }, [users, formsList, selectedForm.form_id]);

  const columns = useMemo(() => [
    {
      title: "Lead Name", dataIndex: "response",
      render: (response: any) => {
        const initials = response?.["Full Name"]?.split(" ").map((w: string) => w[0]).join("").toUpperCase().slice(0, 2) || "NA";
        return (
          <div className="d-flex align-items-center">
            <div className="avatar avatar-md bg-info text-white fw-semibold rounded-circle d-flex align-items-center justify-content-center me-2">{initials}</div>
            <h6 className="fw-medium mb-0" style={{ whiteSpace: "nowrap" }}>{response?.["Full Name"] || "N/A"}</h6>
          </div>
        );
      },
      sorter: (a: any, b: any) => (a.response?.["Full Name"] || "").localeCompare(b.response?.["Full Name"] || ""),
    },
    { title: "Mobile Number", dataIndex: "mobile_no", render: (text: string) => text || "N/A" },
    { title: "Email", dataIndex: "email", render: (text: string) => text || "N/A" },
    { title: "Assigned To", dataIndex: "assigned_to_name", render: (text: string) => text || "N/A" },
    {
      title: "Pipeline Stage", dataIndex: "pipeline_char", render: (text: string) => <span className="badge"
        style={{ backgroundColor: "#e6f4ff", color: "#1677ff", padding: "4px 8px", fontWeight: 500 }}>{text || "N/A"}</span>
    },
    { title: "Lead Source", dataIndex: "lead_source", render: (text: string) => text || "N/A" },
    {
      title: "Last Call", dataIndex: "latest_call_date",
      render: (_: any, record: any) => record.latest_call_date
        ? <div><div style={{ fontWeight: 500 }}>{moment(record.latest_call_date).format("DD MMM YYYY")}</div><small className="text-muted"><i className="ti ti-clock me-1" style={{ fontSize: 12 }} />{moment(record.latest_call_date).format("hh:mm A")}</small></div>
        : <span className="text-muted"><i className="ti ti-phone-off me-1" />No calls yet</span>,
    },
    {
      title: "Actions", dataIndex: "actions", fixed: "right" as const,
      render: (_: any, record: any) => {
        const isCallActive = !!call.activeCall;
        const callBtnTitle = isCallActive
          ? "End current call first"
          : "Call";
        return (
          <div className="d-flex gap-2">
            <Link to="#"
              onClick={(e) => { e.preventDefault(); setActivityModalLead({ id: record?.id, key: record.key || record.lead_id, name: record.response?.["Full Name"] || "N/A", mobile_no: record.mobile_no }); }}
              title="View Activity">
              <span className="badge badge-sm bg-transparent-purple text-purple">
                <i className="ti ti-eye " />
              </span>
            </Link>
            <Link to="#"
              onClick={(e) => { e.preventDefault(); window.open(`https://wa.me/${record.mobile_no?.replace(/\D/g, "")}`, "_blank"); }}
              className="btn btn-sm btn-icon btn-success" title="WhatsApp">
              <i className="ti ti-brand-whatsapp" />
            </Link>
            <button
              className="btn btn-sm btn-icon btn-primary"
              disabled={isCallActive}
              title={callBtnTitle}
              style={{ opacity: isCallActive ? 0.45 : 1, cursor: isCallActive ? "not-allowed" : "pointer" }}
              onClick={() => {
                if (isCallActive) return;
                isAutoCallRef.current = false;
                dispatch(callActions.setIsAutoCall(false));
                handleCall(record);
              }}
            >
              <i className="ti ti-phone" />
            </button>
          </div>
        );
      },
    },
  ], [handleCall]);

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <>
      <div className="page-wrapper">
        {isLoading ? (
          <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "90vh" }}>
            <div className="text-center">
              <div className="spinner-border text-primary" role="status" style={{ width: "3rem", height: "3rem" }}><span className="visually-hidden">Loading...</span></div>
              <p className="mt-3 text-muted">Loading Leads...</p>
            </div>
          </div>
        ) : (
          <div className="content">
            <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
              <div className="my-auto mb-2">
                <h2 className="mb-1">Leads Call</h2>
                <nav><ol className="breadcrumb mb-0"><li className="breadcrumb-item"><Link to={`${all_routes.leadsCall}${location?.search || ""}`}><i className="ti ti-smart-home" /></Link></li><li className="breadcrumb-item">CRM</li><li className="breadcrumb-item active" aria-current="page">Leads Call</li></ol></nav>
              </div>
              <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
                <div className="me-3 mb-2"><Link to={`${all_routes.liveCall}${location?.search || ""}`} className="btn btn-primary"><i className="ti ti-headset me-1" />Go To Live Call</Link></div>
                <div className="me-3 mb-2">
                  <button
                    className="btn btn-outline-success"
                    disabled={filteredLeads.length === 0 || !!call.activeCall}
                    title={call.activeCall ? "End current call before starting auto call" : ""}
                    onClick={() => {
                      if (call.activeCall) return;
                      isAutoCallRef.current = true;
                      autoCallStoppedRef.current = false;
                      dispatch(callActions.setIsAutoCall(true));
                      dispatch(callActions.setAutoCallStopped(false));
                      handleCall(filteredLeads[0]);
                    }}
                  >
                    <i className="ti ti-player-play me-1" />Start Auto Call
                  </button>

                </div>

                <div className="me-3 mb-2" style={{ minWidth: 200 }}>
                  <CommonSelect
                    key={`form-${selectedCompany.companyId}`}
                    options={formOptions}
                    defaultValue={selectedForm.form_id ? { value: `${selectedForm.pipeline_id}-${selectedForm.form_id}`, label: selectedForm.form_name } : undefined}
                    onChange={handleFormChangeFromHeader}
                    placeHolder={formsList.length === 0 ? "No forms available" : "Select Lead Form"} isDisabled={!selectedCompany.companyId || formsList.length === 0 || !!call.isAutoCall && !!call.activeCall} />
                </div>
                <div className="me-3 mb-2">
                  <CommonSelect
                    key={`company-${companyList.length}`}
                    options={companyList.map((c) => ({ value: c.id, label: `${c.name} - ${c.type}` }))} defaultValue={selectedCompany.companyId ? { value: selectedCompany.companyId, label: `${selectedCompany.companyName} - ${selectedCompany.companyType}` } : undefined} onChange={handleCompanyChange}
                    isDisabled={!!call.isAutoCall && !!call.activeCall}
                    placeHolder="Select Company" />
                </div>
                <div className="ms-2 head-icons"><CollapseHeader /></div>
              </div>
            </div>

            <div className="mt-4">
              <div className="card" style={{ overflow: "hidden", gap: 6, backgroundColor: "transparent", border: "none", boxShadow: "none" }}>
                <div className="slide-in-left lc-header-container">
                  <div className="lc-header-inner">
                    {/* LEFT SIDE */}
                    <div className="lc-left-actions">
                      <h5 className="mb-0">Lead Management</h5>
                    </div>

                    {/* RIGHT SIDE */}
                    <div className="lc-right-actions">
                      {/* Pipeline */}
                      <div style={{ minWidth: 160 }}>
                        <select className="form-select" value={filters.pipelineStage} disabled={!selectedForm.form_id} onChange={(e) => setFilters((p) => ({ ...p, pipelineStage: e.target.value }))}>
                          {pipelineStageOptions.map((opt, i) => <option key={i} value={opt.value}>{opt.label}</option>)}
                        </select>
                      </div>
                      <div className="lc-right-actions" style={{ position: "relative", zIndex: 9999 }}>
                        <ReminderDropdown
                          followUpFilter={followUpFilter}
                          setFollowUpFilter={setFollowUpFilter}
                          reminderLeadIds={reminderLeadIds}
                          isReminderLoading={isReminderLoading}
                          reminderCustomStart={reminderCustomStart}
                          reminderCustomEnd={reminderCustomEnd}
                          setReminderCustomStart={setReminderCustomStart}
                          setReminderCustomEnd={setReminderCustomEnd}
                          onOpen={() => setReminderDropdownOpened(true)}
                        />
                      </div>
                      {/* Filter Toggle */}
                      <button onClick={() => setShowCallFilters((v) => !v)} className="lc-filter-toggle">
                        <SlidersHorizontal size={16} />
                        {showCallFilters ? "Hide Filters" : "Show Filters"}
                      </button>

                      {/* Clear Filters */}
                      <button onClick={handleClearFilters} className="lc-filter-toggle" style={{ borderColor: "#fecaca", background: "#fef2f2", color: "#dc2626" }}>
                        <i className="ti ti-x" />
                        Reset Filters
                      </button>
                    </div>
                  </div>
                </div>

                {/* Below header: filter and table in a row */}
                <div className="livecall-layout align-top">
                  <div className="livecall-table-section">
                    <div className="card-body p-0 slide-up-enter lc-table-card">
                      {!selectedCompany.companyId ? <div className="d-flex justify-content-center align-items-center p-5"><p className="text-muted">Please select a company to view leads</p></div>
                        : !selectedForm.form_id ? <div className="d-flex justify-content-center align-items-center p-5"><p className="text-muted">Please select a lead form to view leads</p></div>
                          : <Datatable dataSource={filteredLeads} columns={columns} Selection={false} />}
                    </div>
                  </div>

                  {/* Filter Sidebar */}
                  <div className={`slide-in-left livecall-filters-section${showCallFilters ? "" : " hidden"}`}>
                    <LeadsGridFilters
                      showFilters={showCallFilters}
                      userRole={user?.userDetails?.role_char?.toLowerCase() || ""}
                      userChoose={userChoose}
                      followUpOptions={followUpOptions}
                      activityFilterOptions={activityFilterOptions}
                      formDataFormChoose={{ user_id: filters.user_id, user_name: filters.user_name }}
                      followUpFilter={followUpFilter}
                      customFollowUpDate={customFollowUpDate}
                      activityFilter={activityFilter}
                      activityStartDate={activityStartDate}
                      activityEndDate={activityEndDate}
                      sortBy={sortBy}
                      selectedStartDate={filters.startDate}
                      selectedEndDate={filters.endDate}
                      searchQuery={filters.searchQuery}
                      tempSearchQuery={tempSearchQuery}
                      isActivityLoading={isActivityLoading}
                      activeFilterCount={activeFilterCount}
                      showOnlyTransferred={filters.showOnlyTransferred}
                      selectedLeadSource={filters.leadSource}
                      leadSource={LEAD_SOURCE_OPTIONS}
                      setShowOnlyTransferred={(val) => setFilters(p => ({ ...p, showOnlyTransferred: val }))}
                      handleLeadSourceChange={(val) => setFilters(p => ({ ...p, leadSource: val }))}
                      handleUserChange={(opt: any) => setFilters(p => ({ ...p, user_id: opt?.value || "0", user_name: opt?.label || "All Employees" }))}
                      setFollowUpFilter={setFollowUpFilter}
                      setCustomFollowUpDate={setCustomFollowUpDate}
                      handleActivityFilterChange={(option: any) => setActivityFilter(option.value)}
                      handleActivityDateChange={(start, end) => { setActivityStartDate(start); setActivityEndDate(end); }}
                      setSortBy={setSortBy}
                      handleDateChange={(start, end) => setFilters(p => ({ ...p, startDate: start, endDate: end }))}
                      setTempSearchQuery={setTempSearchQuery}
                      handleSearchApply={handleSearchApply}
                      handleResetFilters={handleClearFilters}
                      showSortBy={false}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        <div className="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3">
          <p className="mb-0">2025 © CMS.</p>
          <p>Designed & Developed By <Link to="#" className="text-primary" onClick={(e: any) => e.preventDefault()}>ZYVO</Link></p>
        </div>
      </div>

      <LeadActivityModal open={!!activityModalLead} lead={activityModalLead} signature={signature} onClose={() => setActivityModalLead(null)} />
    </>
  );
};

export default LeadsCallPage;
